/**
 * Copyright (c) 2018 Mathis Zeiher
 *
 * Permission to use, copy, modify, and distribute this software for any
 * purpose with or without fee is hereby granted, provided that the above
 * copyright notice and this permission notice appear in all copies.
 *
 * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
 * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
 * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
 * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
 * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
 * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
 * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
 */

/* globals Promise window */
import { symbolComponentState } from '../customelement.js';
import { renderToShadowDOM } from './shadowDOMRenderer.js';
import { renderToLightDOM } from './lightDOMRenderer.js';

/**
 * renders the component
 *
 * @param { CustomElement } this - ref
 * @param { TemplateInstance } fallback - fallback
 *
 */
export function renderComponent(fallback = null) {
  // Fixes bug coming with chrome 94 (but also with other browsers): render is called before componentConnected call, which leads to bugs (e.g. dashboard-elements legend layout)
  // Before a render was executed, even if the custom-element is not yet connected to the dom.
  // the html spec explicitly says, that render calls (upgrades) only should be done with connected components:
  // See: https://html.spec.whatwg.org/multipage/custom-elements.html#custom-elements-upgrades-examples
  if (!this.isConnected) {
    return;
  }

  if (fallback) {
    // if fallback defined render fallback and skip callbacks
    this[symbolComponentState] |= 0b1000;
    const elementToRender = this.renderToElement();

    if (elementToRender === this.shadowRoot) {
      // render to shadowroot
      renderToShadowDOM.apply(this, [elementToRender, fallback]);
    } else {
      renderToLightDOM.apply(this, [elementToRender, fallback]);
    }

    this[symbolComponentState] &= 0b0111;
  } else {
    this[symbolComponentState] |= 0b1000;
    this.componentWillRender();
    const elementToRender = this.renderToElement();

    if (elementToRender === this.shadowRoot) {
      // render to shadowroot
      renderToShadowDOM.apply(this, [elementToRender]);
    } else {
      renderToLightDOM.apply(this, [elementToRender]);
    }

    this.componentDidRender();

    if (this._firstRender) {
      // we need to reset _renderScheduled here due to compatibility reasons -> sometime a scheduleRender is done after firstRender this has to go through
      this._renderScheduled = false;
      this.componentFirstRender();
      this._firstRender = false;
    }

    this._renderCompletedCallbacks.forEach(value => value());

    this._renderCompletedCallbacks = [];
    this[symbolComponentState] &= 0b0110; // reset dirty flag

    if (this._layoutRAFReference === null) {
      // queue a promise which resolves after browser layouting
      // DAVIBE-724: Make sure to bind our componentDidLayout callback before
      // calling it. If you omit this, memory will not get collected properly.
      const triggerDidLayout = function () {
        this.componentDidLayout();
        this._layoutRAFReference = null;
      }.bind(this); // DAVIBE-724: requestAnimationFrame will not fire when the browser
      // window is not flagged as visible. This creates a memory leak
      // because the layoutRAFReference will never get cleaned up when
      // the window was visible before. Because of this, we only schedule
      // a new microtask if the document is currently visible (e.g. not hidden)
      // behind other windows or an inactive tab. When we are hidden, we just
      // run the callback so we do not cause any side effects.


      if (document.visibilityState === 'visible') {
        this._layoutRAFReference = window.requestAnimationFrame(() => {
          Promise.resolve().then(triggerDidLayout);
        });
      } else {
        // Make sure to call the callback reguardless of visibility.
        triggerDidLayout();
      }
    }
  }
}
